import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  TrendingUp, Download, Smartphone, Monitor, Apple, 
  Chrome, Share, Plus, MoreVertical, CheckCircle, ArrowRight
} from 'lucide-react';

export default function DownloadPage() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [device, setDevice] = useState('desktop');

  useEffect(() => {
    // Detect device type
    const userAgent = navigator.userAgent.toLowerCase();
    if (/iphone|ipad|ipod/.test(userAgent)) {
      setDevice('ios');
    } else if (/android/.test(userAgent)) {
      setDevice('android');
    } else {
      setDevice('desktop');
    }

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    // Listen for install prompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setIsInstalled(true);
      }
      setDeferredPrompt(null);
    }
  };

  const features = [
    { icon: <TrendingUp className="w-5 h-5" />, text: 'Real-time price updates' },
    { icon: <Smartphone className="w-5 h-5" />, text: 'Native app experience' },
    { icon: <CheckCircle className="w-5 h-5" />, text: 'Quick account switching' },
    { icon: <Download className="w-5 h-5" />, text: 'Works offline' },
  ];

  return (
    <div className="min-h-screen bg-[#09090B]">
      {/* Header */}
      <header className="border-b border-zinc-800">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-white font-['Outfit']">GeneiProFx</span>
          </Link>
          <Link to="/login">
            <Button variant="outline" size="sm">Login</Button>
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full text-primary text-sm font-medium mb-6">
            <Download className="w-4 h-4" />
            Download Mobile App
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Trade Anywhere with <span className="text-primary">GeneiProFx</span>
          </h1>
          
          <p className="text-zinc-400 text-lg mb-8 max-w-2xl mx-auto">
            Install our mobile app for the best trading experience. Access your accounts, manage trades, and monitor markets on the go.
          </p>

          {/* Features */}
          <div className="flex flex-wrap justify-center gap-4 mb-10">
            {features.map((feature, index) => (
              <div key={index} className="flex items-center gap-2 px-4 py-2 bg-zinc-900/50 rounded-lg text-zinc-300 text-sm">
                <span className="text-primary">{feature.icon}</span>
                {feature.text}
              </div>
            ))}
          </div>

          {/* Install Button */}
          {isInstalled ? (
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-green-500/20 rounded-xl text-green-400">
              <CheckCircle className="w-5 h-5" />
              App Already Installed
            </div>
          ) : deferredPrompt ? (
            <Button 
              size="lg" 
              className="bg-primary hover:bg-primary/90 text-lg px-8 py-6"
              onClick={handleInstallClick}
            >
              <Download className="w-5 h-5 mr-2" />
              Install App Now
            </Button>
          ) : null}
        </div>
      </section>

      {/* Installation Instructions */}
      <section className="py-12 px-4 bg-zinc-900/30">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-white text-center mb-8">
            How to Install
          </h2>

          <div className="grid md:grid-cols-3 gap-6">
            {/* iOS Instructions */}
            <div className={`glass-card p-6 ${device === 'ios' ? 'ring-2 ring-primary' : ''}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-zinc-800 flex items-center justify-center">
                  <Apple className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">iPhone / iPad</h3>
                  <span className="text-zinc-500 text-sm">Safari Browser</span>
                </div>
              </div>
              
              <ol className="space-y-3 text-sm">
                <li className="flex items-start gap-3 text-zinc-300">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center text-xs font-bold">1</span>
                  <span>Open this page in <strong>Safari</strong></span>
                </li>
                <li className="flex items-start gap-3 text-zinc-300">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center text-xs font-bold">2</span>
                  <span>Tap the <Share className="inline w-4 h-4" /> Share button</span>
                </li>
                <li className="flex items-start gap-3 text-zinc-300">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center text-xs font-bold">3</span>
                  <span>Scroll and tap <strong>"Add to Home Screen"</strong></span>
                </li>
                <li className="flex items-start gap-3 text-zinc-300">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center text-xs font-bold">4</span>
                  <span>Tap <strong>"Add"</strong> to install</span>
                </li>
              </ol>
            </div>

            {/* Android Instructions */}
            <div className={`glass-card p-6 ${device === 'android' ? 'ring-2 ring-primary' : ''}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-zinc-800 flex items-center justify-center">
                  <Smartphone className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Android</h3>
                  <span className="text-zinc-500 text-sm">Chrome Browser</span>
                </div>
              </div>
              
              <ol className="space-y-3 text-sm">
                <li className="flex items-start gap-3 text-zinc-300">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center text-xs font-bold">1</span>
                  <span>Open this page in <strong>Chrome</strong></span>
                </li>
                <li className="flex items-start gap-3 text-zinc-300">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center text-xs font-bold">2</span>
                  <span>Tap <MoreVertical className="inline w-4 h-4" /> menu (3 dots)</span>
                </li>
                <li className="flex items-start gap-3 text-zinc-300">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center text-xs font-bold">3</span>
                  <span>Tap <strong>"Install app"</strong> or <strong>"Add to Home screen"</strong></span>
                </li>
                <li className="flex items-start gap-3 text-zinc-300">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center text-xs font-bold">4</span>
                  <span>Tap <strong>"Install"</strong> to confirm</span>
                </li>
              </ol>
            </div>

            {/* Desktop Instructions */}
            <div className={`glass-card p-6 ${device === 'desktop' ? 'ring-2 ring-primary' : ''}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-zinc-800 flex items-center justify-center">
                  <Monitor className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Desktop</h3>
                  <span className="text-zinc-500 text-sm">Chrome / Edge</span>
                </div>
              </div>
              
              <ol className="space-y-3 text-sm">
                <li className="flex items-start gap-3 text-zinc-300">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center text-xs font-bold">1</span>
                  <span>Look for <Plus className="inline w-4 h-4" /> install icon in address bar</span>
                </li>
                <li className="flex items-start gap-3 text-zinc-300">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center text-xs font-bold">2</span>
                  <span>Or click <MoreVertical className="inline w-4 h-4" /> menu → <strong>"Install GeneiProFx"</strong></span>
                </li>
                <li className="flex items-start gap-3 text-zinc-300">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center text-xs font-bold">3</span>
                  <span>Click <strong>"Install"</strong> in the popup</span>
                </li>
                <li className="flex items-start gap-3 text-zinc-300">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center text-xs font-bold">4</span>
                  <span>App opens in its own window</span>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* App Preview */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-white mb-4">
            Full Trading Platform in Your Pocket
          </h2>
          <p className="text-zinc-400 mb-8">
            Access all features of GeneiProFx from your mobile device
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { title: 'Live Charts', desc: 'Real-time price charts with multiple timeframes' },
              { title: 'One-Tap Trading', desc: 'Open and close positions instantly' },
              { title: 'Account Management', desc: 'Switch between accounts seamlessly' },
              { title: 'Push Notifications', desc: 'Get alerts for important events' },
            ].map((item, index) => (
              <div key={index} className="glass-card p-4 text-left">
                <h3 className="text-white font-medium mb-1">{item.title}</h3>
                <p className="text-zinc-500 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-12">
            <Link to="/login">
              <Button size="lg" className="bg-primary hover:bg-primary/90">
                Get Started Now
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-800 py-8 px-4">
        <div className="max-w-4xl mx-auto text-center text-zinc-500 text-sm">
          <p>&copy; 2024 GeneiProFx. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
